import React, { useEffect, useState } from 'react';
import { T } from 'react-translator-component';
import moment from 'moment';
import { Spin, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';

import { getTenants } from '../../../hooks/tenants';
import doRawRequest from '../../../services/rawRequest';
import { parserTenantsForSelect } from '../../../utils/log/logSelect';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import EmptySearch from '../../organisms/EmptySearch';
import { Select } from '../../organisms/Select';
import { downloadTodayLogs } from '../../../hooks/logs';
import RangePickerCustom from '../../atoms/LegacyRangePickerCustom';

import { ScopedStyle, Div, ButtonHeader, Overlay } from './styled';

const LogPage = () => {
  const theme = useTheme();

  const [logs, setLogs] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tenantList, setTenantList] = useState([]);
  const [tenantInputValue, setTenantInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState([null, null]);
  const [value, setValue] = useState([null, null]);
  const [downloading, setDownloading] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);

  const antIcon = (
    <LoadingOutlined style={{ fontSize: 32, color: theme.primary }} spin />
  );

  const handleDownloadTodayLog = async () => {
    setDownloading(true);
    const logDownload = await downloadTodayLogs({
      tenant: tenantInputValue,
      dateFrom,
      dateTo,
    });

    if (logDownload.type === 'error') {
      message.error('Não foi possível fazer o download dos logs do dia');
    }

    setDownloading(false);
  };

  useEffect(() => {
    const downloadWithDates = async () => {
      if (tenantInputValue && dateFrom && dateTo) {
        setOverlayVisible(true);
        await handleDownloadTodayLog();
        setOverlayVisible(false);
      }
    };
    downloadWithDates();
  }, [dateFrom, dateTo]);

  const disabledDate = (current) => {
    if (!dates) return false;
    const tooLate = dates?.[0] && current.diff(dates?.[0], 'days') >= 5;
    const tooEarly = dates?.[1] && dates?.[1].diff(current, 'days') >= 5;
    const fourMonthsAgo = moment().subtract(4, 'months');

    return (
      !!tooEarly ||
      !!tooLate ||
      current.valueOf() > Date.now() ||
      current.isBefore(fourMonthsAgo, 'day')
    );
  };

  const onOpenChange = (open) => {
    if (open) setDates([null, null]);
    else setDates(null);
  };

  const handleClearFilters = () => {
    setDates([null, null]);
    setValue([null, null]);
    setDateFrom('');
    setDateTo('');
  };

  const fetchTenants = async () => {
    setLoading(true);
    const tenants = await getTenants();
    setTenantList(tenants.list);
    setTenantInputValue(tenants.list[0]?.name || '');
    setLoading(false);
  };

  const fetchLogs = async () => {
    if (!tenantInputValue) return;
    setLoading(true);
    const response = await doRawRequest(`log/?tenant=${tenantInputValue}`, {
      method: 'GET',
    });
    setLogs(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    handleClearFilters();
    fetchLogs();
  }, [tenantInputValue]);

  const logColors = {
    '[INFO ]': 'lightgreen',
    '[WARN ]': 'orange',
    '[ERROR]': 'red',
    '[DEBUG]': 'purple',
  };

  const prefixes = Object.keys(logColors);

  const colorizeLog = (log) => {
    if (!log) return [];

    const lines = log.split('\n');

    return lines.map((line, index) => {
      for (let i = 0; i < prefixes.length; i++) {
        const prefix = prefixes[i];
        if (line.startsWith(prefix)) {
          return (
            <div key={index}>
              <span style={{ color: logColors[prefix] }}>{prefix}</span>
              {line.substring(prefix.length)}
            </div>
          );
        }
      }

      return <div key={index}>{line}</div>;
    });
  };

  return (
    <Div>
      <ScopedStyle />
      {overlayVisible && (
        <Overlay>
          <div className='box'>
            <Spin indicator={antIcon} />
            <div className='overlay-text'>
              {dateFrom && dateTo && (
                <p className='dates'>
                  {moment(dateFrom).format('DD/MM/YYYY')} -{' '}
                  {moment(dateTo).format('DD/MM/YYYY')}
                </p>
              )}
              <p className='message'>{T('Downloading logs for viewing...')}</p>
            </div>
          </div>
        </Overlay>
      )}

      <div className='header_row'>
        <div className='top-row'>
          <span className='date-range-label info-text'>
            {T('Select a date range to download old logs:')}
          </span>

          <RangePickerCustom
            className='range_picker'
            format='DD/MM/YYYY'
            value={dates || value}
            disabledDate={disabledDate}
            onCalendarChange={(val) => setDates(val)}
            onChange={(val) => {
              setDateFrom(
                val?.length && val[0]
                  ? moment(val[0]).format('YYYY-MM-DD')
                  : '',
              );
              setDateTo(
                val?.length && val[1]
                  ? moment(val[1]).format('YYYY-MM-DD')
                  : '',
              );
              setValue(val);
            }}
            onOpenChange={onOpenChange}
            allowClear
          />
        </div>

        <div className='bottom-row'>
          <h1 className='page_title'>
            Logs <span className='date-range-label'>{T('recent')}</span>
          </h1>

          <div className='controls-group'>
            <Select
              value={tenantInputValue}
              options={parserTenantsForSelect(tenantList)}
              action={(value) => setTenantInputValue(value)}
              ordered
            />

            <ButtonHeader onClick={handleDownloadTodayLog}>
              {downloading ? T('Downloading...') : T('Download log')}
            </ButtonHeader>

            <ButtonHeader onClick={handleClearFilters}>
              {T('Clear filters')}
            </ButtonHeader>
          </div>
        </div>
      </div>

      {loading ? (
        <ListSkeleton
          style={{ paddingTop: 20, paddingLeft: 20, paddingRight: 20 }}
        />
      ) : !logs ? (
        <EmptySearch />
      ) : logs === 'Not available' ? (
        <p className='error_message' role='alert'>
          {T('Not Available')}
        </p>
      ) : (
        <div className='content'>{logs && colorizeLog(logs)}</div>
      )}
    </Div>
  );
};

export default LogPage;
